import React, {useState, useEffect} from "react";
import {Row, Col, Badge, Button, Dropdown, Modal, Image} from "react-bootstrap";
import {Link} from "react-router-dom";
import {
  FaUserCircle,
  FaCog,
  FaSignOutAlt,
  FaBell,
  FaQuestionCircle,
  FaLifeRing,
  FaCircle,
  FaBullhorn,
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaExpand,
  FaCompress,
  FaClock,
} from "react-icons/fa";
import userInfo from "../data/user.json"; // Importar datos del usuario

// Componente de notificaciones simplificado
const NotificationsDropdown = ({notifications, onNotificationClick}) => {
  const [showAllNotifications, setShowAllNotifications] = useState(false);
  const unreadCount = notifications.filter((n) => !n.read).length;
  const recentNotifications = notifications.slice(0, 5);

  const getNotificationIcon = (type) => {
    switch (type) {
      case "success":
        return <FaCheckCircle className="text-success" />;
      case "warning":
        return <FaExclamationTriangle className="text-warning" />;
      case "error":
        return <FaExclamationTriangle className="text-danger" />;
      default:
        return <FaInfoCircle className="text-info" />;
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));

    if (diffInMinutes < 1) return "Ahora";
    if (diffInMinutes < 60) return `${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
    return `${Math.floor(diffInMinutes / 1440)}d`;
  };

  const markAllAsRead = () => {
    notifications.forEach((n) => {
      if (!n.read) onNotificationClick(n.id);
    });
  };

  return (
    <>
      <Dropdown align="end">
        <Dropdown.Toggle
          variant="link"
          className="btn-notification position-relative p-2 border-0 bg-transparent"
          style={{boxShadow: "none"}}
        >
          <FaBell size={18} className="text-secondary" />
          {unreadCount > 0 && (
            <Badge
              pill
              bg="danger"
              className="position-absolute top-0 start-100 translate-middle"
              style={{fontSize: "0.6em", padding: "0.3em 0.5em"}}
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </Badge>
          )}
        </Dropdown.Toggle>

        <Dropdown.Menu
          className="shadow-lg border-0"
          style={{width: "350px", maxHeight: "400px"}}
        >
          <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
            <h6 className="mb-0 fw-bold">Notificaciones</h6>
            {unreadCount > 0 && (
              <Button
                variant="link"
                size="sm"
                onClick={markAllAsRead}
                className="p-0 text-decoration-none"
              >
                <FaCheckCircle className="me-1" />
                Marcar como leídas
              </Button>
            )}
          </div>

          <div style={{maxHeight: "300px", overflowY: "auto"}}>
            {recentNotifications.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <FaBell size={24} className="mb-2 opacity-50" />
                <p className="mb-0">No hay notificaciones</p>
              </div>
            ) : (
              recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 border-bottom cursor-pointer ${
                    !notification.read ? "bg-light" : ""
                  }`}
                  onClick={() => onNotificationClick(notification.id)}
                >
                  <div className="d-flex align-items-start">
                    <div className="me-2 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-1">
                        <h6
                          className={`mb-1 small ${
                            !notification.read ? "fw-bold" : "fw-medium"
                          }`}
                        >
                          {notification.title || notification.message}
                        </h6>
                        <small className="text-muted">
                          {getTimeAgo(notification.timestamp)}
                        </small>
                      </div>
                      {notification.message && notification.title && (
                        <p className="mb-0 text-muted small">
                          {notification.message}
                        </p>
                      )}
                    </div>
                    {!notification.read && (
                      <FaCircle size={6} className="text-primary ms-1 mt-2" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {notifications.length > 5 && (
            <div className="p-2 border-top text-center">
              <Button
                variant="link"
                size="sm"
                className="text-decoration-none"
                onClick={() => setShowAllNotifications(true)}
              >
                Ver todas ({notifications.length})
              </Button>
            </div>
          )}
        </Dropdown.Menu>
      </Dropdown>

      {/* Modal para todas las notificaciones */}
      <Modal
        show={showAllNotifications}
        onHide={() => setShowAllNotifications(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Todas las Notificaciones</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{maxHeight: "60vh", overflowY: "auto"}}>
          {notifications.map((notification) => (
            <div
              key={notification.id}
              className={`p-3 border-bottom ${
                !notification.read ? "bg-light" : ""
              }`}
            >
              <div className="d-flex align-items-start">
                <div className="me-3">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-start mb-1">
                    <h6
                      className={`mb-1 ${
                        !notification.read ? "fw-bold" : "fw-medium"
                      }`}
                    >
                      {notification.title || notification.message}
                    </h6>
                    <small className="text-muted">
                      {getTimeAgo(notification.timestamp)}
                    </small>
                  </div>
                  {notification.message && notification.title && (
                    <p className="mb-0 text-muted">{notification.message}</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </Modal.Body>
      </Modal>
    </>
  );
};

// Componente de menú de usuario simplificado
const UserDropdown = ({onLogout}) => {
  const [fullScreen, setFullScreen] = useState(false);
  const toggleFullScreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setFullScreen(true);
    } else {
      document.exitFullscreen();
      setFullScreen(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "online":
        return "success";
      case "away":
        return "warning";
      case "busy":
        return "danger";
      default:
        return "secondary";
    }
  };

  return (
    <Dropdown align="end">
      <Dropdown.Toggle
        variant="link"
        className="btn-user d-flex align-items-center p-2 border-0 bg-transparent text-decoration-none"
        style={{boxShadow: "none"}}
      >
        <div className="position-relative me-2">
          {userInfo.avatar ? (
            <img
              src={userInfo.avatar}
              alt="Avatar"
              className="rounded-circle"
              width="28"
              height="28"
            />
          ) : (
            <div
              className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
              style={{width: "28px", height: "28px"}}
            >
              <span className="fw-bold small">
                {userInfo.name
                  .split(" ")
                  .map((n) => n[0])
                  .join("")}
              </span>
            </div>
          )}
          <Badge
            pill
            bg={getStatusColor(userInfo.status)}
            className="position-absolute bottom-0 end-0 border border-white"
            style={{width: "8px", height: "8px", padding: 0}}
          />
        </div>
        <div className="text-start d-none d-md-block">
          <div className="fw-medium text-dark" style={{fontSize: "0.85rem"}}>
            {userInfo.name.split(" ")[0]} {userInfo.name.split(" ")[1]}
          </div>
          <div className="text-muted" style={{fontSize: "0.75rem"}}>
            {userInfo.role}
          </div>
        </div>
      </Dropdown.Toggle>

      <Dropdown.Menu className="shadow-lg border-0" style={{width: "260px"}}>
        {/* Información del usuario */}
        <div className="p-3 border-bottom">
          <div className="d-flex align-items-center">
            <div className="position-relative me-3">
              {userInfo.avatar ? (
                <img
                  src={userInfo.avatar}
                  alt="Avatar"
                  className="rounded-circle"
                  width="40"
                  height="40"
                />
              ) : (
                <div
                  className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                  style={{width: "40px", height: "40px"}}
                >
                  <span className="fw-bold">
                    {userInfo.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </span>
                </div>
              )}
              <Badge
                pill
                bg={getStatusColor(userInfo.status)}
                className="position-absolute bottom-0 end-0 border border-white"
                style={{width: "10px", height: "10px", padding: 0}}
              />
            </div>
            <div className="flex-grow-1">
              <h6 className="mb-1 fw-bold small">{userInfo.name}</h6>
              <p className="mb-1 text-muted" style={{fontSize: "0.75rem"}}>
                {userInfo.email}
              </p>
              <Badge bg="primary" style={{fontSize: "0.65rem"}}>
                {userInfo.role}
              </Badge>
            </div>
          </div>
          <div className="mt-2 text-muted" style={{fontSize: "0.75rem"}}>
            <FaClock className="me-1" />
            Último acceso: {userInfo.lastLogin}
          </div>
        </div>

        {/* Acciones principales */}
        <div className="py-1">
          <Dropdown.Item
            as={Link}
            to="/profile"
            className="d-flex align-items-center py-2"
          >
            <FaUserCircle className="me-3 text-primary" />
            <div>
              <div className="fw-medium small">Mi Perfil</div>
              <small className="text-muted">Información personal</small>
            </div>
          </Dropdown.Item>

          <Dropdown.Item
            as={Link}
            to="/settings"
            className="d-flex align-items-center py-2"
          >
            <FaCog className="me-3 text-secondary" />
            <div>
              <div className="fw-medium small">Configuración</div>
              <small className="text-muted">Preferencias</small>
            </div>
          </Dropdown.Item>

          <Dropdown.Item
            as={Link}
            to="/help"
            className="d-flex align-items-center py-2"
          >
            <FaQuestionCircle className="me-3 text-info" />
            <div>
              <div className="fw-medium small">Centro de Ayuda</div>
              <small className="text-muted">Documentación</small>
            </div>
          </Dropdown.Item>

          <Dropdown.Item
            as={Link}
            to="/support"
            className="d-flex align-items-center py-2"
          >
            <FaLifeRing className="me-3 text-warning" />
            <div>
              <div className="fw-medium small">Soporte Técnico</div>
              <small className="text-muted">Contactar soporte</small>
            </div>
          </Dropdown.Item>
        </div>

        <Dropdown.Divider />

        {/* Herramientas rápidas */}
        <div className="px-3 py-1">
          <Button
            variant="link"
            size="sm"
            onClick={toggleFullScreen}
            className="d-flex align-items-center p-0 text-decoration-none w-100 justify-content-start"
          >
            {fullScreen ? (
              <FaCompress className="me-2" />
            ) : (
              <FaExpand className="me-2" />
            )}
            <span className="small">
              {fullScreen ? "Salir de" : "Pantalla"} completa
            </span>
          </Button>
        </div>

        <Dropdown.Divider />

        {/* Cerrar sesión */}
        <Dropdown.Item
          onClick={onLogout}
          className="d-flex align-items-center py-2 text-danger"
        >
          <FaSignOutAlt className="me-3" />
          <div>
            <div className="fw-medium small">Cerrar Sesión</div>
            <small className="text-muted">Salir de la plataforma</small>
          </div>
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

// Componente principal del Header simplificado
const Header = ({
  onLogout,
  notifications = [],
  onNotificationClick,
  activeClient,
  clients,
  onClientChange,
}) => {
  // El reloj se movió a su propio componente para evitar que el Header se re-renderice
  // y cierre los dropdowns abiertos.
  const Clock = () => {
    const [time, setTime] = useState(new Date());

    useEffect(() => {
      const timer = setInterval(() => {
        setTime(new Date());
      }, 1000); // Actualizar cada segundo
      return () => clearInterval(timer);
    }, []);

    return (
      // Se elimina el Dropdown y el calendario, dejando solo el reloj.
      <div
        className="d-flex align-items-center p-2 text-decoration-none"
        style={{cursor: "default"}}
      >
        <div className="d-none d-md-flex flex-column align-items-end text-muted small">
          <div className="fw-medium text-dark">
            {time.toLocaleTimeString("es-ES", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>
          <div style={{fontSize: "0.75rem"}}>
            {time.toLocaleDateString("es-ES", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </div>
        </div>
      </div>
    );
  };
  const ClientSwitcher = () => (
    <Dropdown align="end">
      <Dropdown.Toggle
        variant="link"
        className="d-flex align-items-center p-2 border-0 bg-transparent text-decoration-none"
        style={{boxShadow: "none"}}
      >
        <Image
          src={activeClient.logo}
          alt={`${activeClient.name} logo`}
          className="me-2 bg-white rounded"
          style={{width: "32px", height: "32px", objectFit: "contain"}}
        />
        <div className="text-start d-none d-md-block">
          <div className="fw-medium text-dark" style={{fontSize: "0.85rem"}}>
            {activeClient.name}
          </div>
          <div className="text-muted" style={{fontSize: "0.75rem"}}>
            Cliente Activo
          </div>
        </div>
      </Dropdown.Toggle>
      <Dropdown.Menu className="shadow-lg border-0">
        <Dropdown.Header>Cambiar de Cliente</Dropdown.Header>
        {clients.map((client) => (
          <Dropdown.Item
            key={client.id}
            onClick={() => onClientChange(client)}
            className="d-flex align-items-center"
          >
            <Image
              src={client.logo}
              alt={`${client.name} logo`}
              className="me-2 bg-white rounded"
              style={{width: "24px", height: "24px", objectFit: "contain"}}
            />
            {client.name}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );

  return (
    <header
      className="shadow-sm fixed-top d-flex"
      style={{
        zIndex: 1030,
        height: "60px",
        left: 0,
        right: 0,
      }}
    >
      {/* Sección Izquierda (simula ser parte del Sidebar) */}
      <div
        className="d-flex align-items-center px-4"
        style={{
          width: "250px",
          backgroundColor: "#1e293b",
          color: "white",
        }}
      >
        <h4 className="text-white d-flex align-items-center mb-0">
          <FaBullhorn
            className="me-2"
            style={{color: "#0ea5e9", fontSize: "1.5rem"}}
          />
          PorterCom
        </h4>
      </div>

      {/* Sección Derecha (Header principal) */}
      <div className="flex-grow-1 px-4 h-100 bg-white border-bottom border-start">
        <Row className="align-items-center h-100">
          {/* Herramientas del header */}
          <Col xs="auto" className="ms-auto">
            <div className="d-flex align-items-center gap-2">
              {/* 1. Hora actual */}
              <Clock />

              {/* Nuevo Separador */}
              <div
                className="vr d-none d-md-block"
                style={{height: "40px", alignSelf: "center"}}
              ></div>

              {/* 2. Selector de Cliente */}
              {activeClient && <ClientSwitcher />}

              {/* 3. Separador */}
              <div
                className="vr d-none d-md-block"
                style={{height: "40px", alignSelf: "center"}}
              ></div>

              {/* 4. Notificaciones */}
              <NotificationsDropdown
                notifications={notifications}
                onNotificationClick={onNotificationClick}
              />

              {/* 5. Menú de usuario */}
              <UserDropdown onLogout={onLogout} />
            </div>
          </Col>
        </Row>
      </div>
    </header>
  );
};

export default Header;
